import { QuickBooksInvoiceType } from "@/interfaces";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Button, Card, Modal, Table, Tag } from "antd";
import { CreditCard, Download } from "lucide-react";

interface QuickBooksInvoiceDetailsModalProps {
  open: boolean;
  invoice: QuickBooksInvoiceType | null;
  onClose: () => void;
  dateFormat?: string;
  onDownloadPdf?: (invoice: QuickBooksInvoiceType) => void;
  downloadingPdf?: string | null;
  onPay?: (invoice: QuickBooksInvoiceType) => void;
  processingPayment?: string | null;
  status?: "Paid" | "Open" | "Overdue";
}

export default function QuickBooksInvoiceDetailsModal({
  open,
  invoice,
  onClose,
  dateFormat,
  onDownloadPdf,
  downloadingPdf,
  onPay,
  processingPayment,
  status = "Open",
}: QuickBooksInvoiceDetailsModalProps) {
  if (!invoice) return null;

  // Status color mapping
  const statusColorMap: Record<string, string> = {
    Paid: "success",
    Open: "processing",
    Overdue: "error",
  };

  // Filter line items to only show sales items
  const lineItems = (invoice.Line || []).filter(
    (line: any) => line.DetailType === "SalesItemLineDetail" && line.SalesItemLineDetail,
  );

  const lineItemColumns = [
    {
      title: "Product/Service",
      dataIndex: ["SalesItemLineDetail", "ItemRef", "name"],
      key: "productService",
      render: (value: string) => <span className="font-medium">{value || "—"}</span>,
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "description",
      render: (value: string) => <span className="text-gray-600">{value || "—"}</span>,
    },
    {
      title: "Qty",
      dataIndex: ["SalesItemLineDetail", "Qty"],
      key: "quantity",
      align: "center" as const,
      width: 80,
      render: (value: number) => value || 1,
    },
    {
      title: "Rate",
      dataIndex: ["SalesItemLineDetail", "UnitPrice"],
      key: "rate",
      align: "right" as const,
      width: 100,
      render: (value: number) => `$${(value || 0).toLocaleString()}`,
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "amount",
      align: "right" as const,
      width: 120,
      render: (value: number) => <span className="font-semibold">${(value || 0).toLocaleString()}</span>,
    },
  ];

  const canPay = status !== "Paid";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      title="Invoice Details"
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          {onDownloadPdf && (
            <Button
              type="default"
              icon={<Download size={16} />}
              onClick={() => onDownloadPdf(invoice)}
              loading={downloadingPdf === invoice.Id}
            >
              Download PDF
            </Button>
          )}
          {canPay && onPay && (
            <Button
              type="primary"
              icon={<CreditCard size={16} />}
              loading={processingPayment === invoice.Id}
              disabled={processingPayment === invoice.Id}
              onClick={() => onPay(invoice)}
            >
              {processingPayment === invoice.Id ? "Processing..." : "Pay Now"}
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Section */}
        <Card className="bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Invoice Number</p>
              <p className="text-lg font-semibold text-blue-600">{invoice.DocNumber || "—"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-lg font-bold text-gray-900">${Number(invoice.TotalAmt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Balance Due</p>
              <p className={`text-lg font-bold ${invoice.Balance > 0 ? "text-orange-600" : "text-green-600"}`}>
                ${Number(invoice.Balance).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Tag color={statusColorMap[status] || "default"}>{status}</Tag>
            </div>
          </div>
        </Card>

        {/* Customer & Dates Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {invoice.CustomerRef?.name && (
            <div>
              <p className="text-sm text-gray-500">Billed To</p>
              <p className="font-medium">{invoice.CustomerRef.name}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Invoice Date</p>
            <p className="font-medium">{formatCompanyDate(invoice.TxnDate, dateFormat)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Due Date</p>
            <p className={`font-medium ${status === "Overdue" ? "text-red-600" : ""}`}>
              {formatCompanyDate(invoice.DueDate, dateFormat)}
            </p>
          </div>
          {invoice.CurrencyRef?.value && (
            <div>
              <p className="text-sm text-gray-500">Currency</p>
              <p className="font-medium">{invoice.CurrencyRef.value}</p>
            </div>
          )}
        </div>

        {/* Line Items Table */}
        {lineItems.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Line Items</h3>
            <Table
              dataSource={lineItems.map((item: any, index: number) => ({
                ...item,
                key: item.Id || index,
              }))}
              columns={lineItemColumns}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row className="bg-gray-50">
                    <Table.Summary.Cell index={0} colSpan={4} className="text-right font-semibold">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} className="text-right font-bold">
                      ${Number(invoice.TotalAmt).toLocaleString()}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  {invoice.Balance > 0 && (
                    <Table.Summary.Row className="bg-orange-50">
                      <Table.Summary.Cell index={0} colSpan={4} className="text-right font-semibold text-orange-700">
                        Balance Due:
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} className="text-right font-bold text-orange-700">
                        ${Number(invoice.Balance).toLocaleString()}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                </Table.Summary>
              )}
            />
          </div>
        )}

        {/* Private Notes */}
        {invoice.PrivateNote && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">{invoice.PrivateNote}</p>
            </div>
          </div>
        )}

        {/* Billing Email */}
        {invoice.BillEmail?.Address && (
          <div className="text-sm text-gray-500">
            Sent to: <span className="text-gray-700">{invoice.BillEmail.Address}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
